import { Component, effect, inject, input } from "@angular/core";
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	Validators
} from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { HeroComponent } from "@shared/component/hero/hero.component";
import { VerifyPasswordRecoveryDTO } from "@shared/model/user.model";
import { StorageManagerService } from "@shared/services/storage/storage-manager.service";
import { UserApiService } from "@shared/services/users/userApi.service";
import { ClickarUtilsService } from "@shared/services/utils/clickar-utils.service";
@Component({
	selector: "app-change-password",
	templateUrl: "./change-password.component.html",
	styleUrl: "./change-password.component.scss",
	standalone: true,
	imports: [ReactiveFormsModule, TranslateModule, HeroComponent]
})
export class ChangePasswordComponent {
	private userService = inject(UserApiService);
	protected utils = inject(ClickarUtilsService);
	private router = inject(Router);
	private storageService = inject(StorageManagerService);
	protected showPasswords = false;

	tag = input<string>();
	username = input<string>();
	email = input<string>();

	changePasswordForm = new FormGroup({
		password: new FormControl<string>("", {
			nonNullable: true,
			validators: [
				Validators.required,
				Validators.minLength(12),
				Validators.maxLength(20),
				this.passwordPolicyValidator
			]
		}),
		confPassword: new FormControl<string>("", {
			nonNullable: true,
			validators: [Validators.required]
		})
	}, { validators: this.passwordsMatchValidator });

	constructor() {
		effect(() => {
			if (!(this.tag() && this.username())) {
				this.router.navigate(["/"]);
			}
		});
	}

	private passwordPolicyValidator(control: AbstractControl): ValidationErrors | null {
		const value = control.value as string;
		if (value) {
			const validCharactersRegex = /^[a-zA-Z0-9$&%*?=]+$/;
			if (!validCharactersRegex.test(value)) {
				return { invalidCharacters: true };
			}

			if (!(/* hasLowercase */ /[a-z]/.test(value) && /* hasUppercase */ /[A-Z]/.test(value) && /* hasNumbers */ /[0-9]/.test(value))) {
				return { insufficientComplexity: true };
			}
		}
		return null;
	}

	private passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
		const pass = group.get('password')?.value,
			confPass = group.get('confPassword')?.value;
		return pass === confPass ? null : { passwordMismatch: true };
	}

	changePassword() {
		if (!this.changePasswordForm.invalid) {
			this.showPasswords = false;
			const currentTag = this.tag(),
				currentUsername = this.username();

			if (!(currentTag && currentUsername)) {
				this.router.navigate(["/"]);
				return;
			}

			const formValues = this.changePasswordForm.getRawValue(),
				dto: VerifyPasswordRecoveryDTO = {
					...formValues,
					hash: currentTag,
					username: currentUsername
				} as VerifyPasswordRecoveryDTO;

			this.userService.verifyPasswordRecoveryUsingPOST(dto)
				.subscribe(() => {
					this.storageService.removeSessionItem("requested_route"); // NOTE: pruning the Oauth-generated item to be used for redirecting
					// this.router.navigate(["/"]); // NOTE: this would not prevent Oauth to rewrite requested_route
					location.href = "/";
				});
		}
	}

	protected suggestPassword() {
		const pass = this.utils.generatePassword();
		this.changePasswordForm.controls.password.setValue(pass);
		this.changePasswordForm.controls.confPassword.setValue(pass);
		this.showPasswords = true;
	}
}